export type SupplierManagementMembershipOrigin = 'AUTOMATIC_LEGAL_IDENTIFIER' | 'USER_CONFIRMED';

export interface SupplierConsolidationSupplierResult {
  readonly id: string;
  readonly displayName: string;
  readonly email: string | null;
  readonly groupId: string | null;
  readonly groupName: string | null;
  readonly membershipOrigin: SupplierManagementMembershipOrigin | null;
  readonly lastImportedAt: string;
}

export interface SupplierConsolidationGroupResult {
  readonly id: string;
  readonly displayName: string;
  readonly members: readonly SupplierConsolidationSupplierResult[];
}

export interface SupplierConsolidationSuggestionResult {
  readonly id: string;
  readonly supplierReferenceIds: readonly string[];
  readonly proposedName: string;
  readonly reasons: readonly ('SAME_NORMALIZED_NAME' | 'NAME_CONTAINS' | 'SHARED_BRAND_TOKEN')[];
}

export interface SupplierSuggestionExclusionResult {
  readonly id: string;
  readonly leftSupplierReferenceId: string;
  readonly leftSupplierName: string;
  readonly rightSupplierReferenceId: string;
  readonly rightSupplierName: string;
  readonly createdAt: string;
}

export interface SupplierConsolidationResult {
  readonly suppliers: readonly SupplierConsolidationSupplierResult[];
  readonly groups: readonly SupplierConsolidationGroupResult[];
  readonly suggestions: readonly SupplierConsolidationSuggestionResult[];
  readonly exclusions?: readonly SupplierSuggestionExclusionResult[];
}

function isSupplierConsolidationResult(value: unknown): value is SupplierConsolidationResult {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<SupplierConsolidationResult>;
  return (
    Array.isArray(candidate.suppliers) &&
    Array.isArray(candidate.groups) &&
    Array.isArray(candidate.suggestions) &&
    (candidate.exclusions === undefined || Array.isArray(candidate.exclusions))
  );
}

async function request(
  method: 'GET' | 'POST' | 'DELETE',
  path: string,
  csrfToken?: string,
  body?: unknown,
): Promise<SupplierConsolidationResult> {
  const response = await fetch('/api/integrations/freebe/suppliers/consolidation' + path, {
    method,
    credentials: 'same-origin',
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(csrfToken === undefined ? {} : { 'X-CSRF-Token': csrfToken }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) throw new Error('Supplier consolidation request failed.');
  const result: unknown = await response.json();
  if (!isSupplierConsolidationResult(result)) {
    throw new Error('Supplier consolidation response is invalid.');
  }
  return { ...result, exclusions: result.exclusions ?? [] };
}

export const supplierConsolidationApi = {
  async search(query: string, signal?: AbortSignal): Promise<SupplierMergeSearchResult> {
    const response = await fetch(
      '/api/integrations/freebe/suppliers/consolidation/search?q=' + encodeURIComponent(query),
      {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        ...(signal ? { signal } : {}),
      },
    );
    if (!response.ok) throw new Error('Supplier search failed.');
    const value: unknown = await response.json();
    if (
      typeof value !== 'object' ||
      value === null ||
      !('choices' in value) ||
      !Array.isArray(value.choices) ||
      !('hasMore' in value) ||
      typeof value.hasMore !== 'boolean' ||
      value.choices.some((choice: unknown) => {
        if (typeof choice !== 'object' || choice === null) return true;
        const c = choice as Partial<SupplierMergeSearchResult['choices'][number]>;
        return (
          typeof c.displayName !== 'string' ||
          (c.groupId !== null && typeof c.groupId !== 'string') ||
          !Number.isInteger(c.memberCount) ||
          (c.memberCount ?? 0) < 1 ||
          !Array.isArray(c.supplierReferenceIds) ||
          c.supplierReferenceIds.length < 1 ||
          c.supplierReferenceIds.some((id: unknown) => typeof id !== 'string') ||
          !Array.isArray(c.members) ||
          c.members.some(
            (member: unknown) =>
              typeof member !== 'object' ||
              member === null ||
              !('id' in member) ||
              typeof member.id !== 'string' ||
              !('displayName' in member) ||
              typeof member.displayName !== 'string',
          )
        );
      })
    )
      throw new Error('Supplier search response is invalid.');
    return value as SupplierMergeSearchResult;
  },
  consolidateManually(
    input: ManualSupplierMergeInput,
    csrfToken: string,
  ): Promise<SupplierConsolidationResult> {
    return request('POST', '/manual-groups', csrfToken, input);
  },
  read(): Promise<SupplierConsolidationResult> {
    return request('GET', '');
  },
  consolidate(
    displayName: string,
    supplierReferenceIds: readonly string[],
    csrfToken: string,
  ): Promise<SupplierConsolidationResult> {
    return request('POST', '/groups', csrfToken, { displayName, supplierReferenceIds });
  },
  dismissSuggestion(
    supplierReferenceIds: readonly string[],
    csrfToken: string,
  ): Promise<SupplierConsolidationResult> {
    return request('POST', '/suggestion-exclusions', csrfToken, { supplierReferenceIds });
  },
  restoreSuggestion(exclusionId: string, csrfToken: string): Promise<SupplierConsolidationResult> {
    return request(
      'DELETE',
      '/suggestion-exclusions/' + encodeURIComponent(exclusionId),
      csrfToken,
    );
  },
  detach(
    groupId: string,
    supplierReferenceId: string,
    csrfToken: string,
  ): Promise<SupplierConsolidationResult> {
    return request(
      'DELETE',
      '/groups/' +
        encodeURIComponent(groupId) +
        '/members/' +
        encodeURIComponent(supplierReferenceId),
      csrfToken,
    );
  },
};
import type { ManualSupplierMergeInput, SupplierMergeSearchResult } from '@tosho/contracts';
